import {useForm} from 'react-hook-form';
import {useMutation} from '@tanstack/react-query';
import {Trans} from '@ui/i18n/trans';
import {useTrans} from '@ui/i18n/use-trans';
import {message} from '@ui/i18n/message';
import {Form} from '@ui/forms/form';
import {FormTextField} from '@ui/forms/input-field/text-field/text-field';
import {FormSelect, Option} from '@ui/forms/select/select';
import {Button} from '@ui/buttons/button';
import {toast} from '@ui/toast/toast';
import {apiClient} from '@common/http/query-client';
import {onFormQueryError} from '@common/errors/on-form-query-error';
import {StaticPageTitle} from '@common/seo/static-page-title';
import {SitePageLayout} from '@app/site-page-layout';

// PAKET 15.1 — Kariyer sayfası (Kafkas Bey talebi): editör, stajyer ve iş
// birliği başvuruları tek adresten. Backend: POST career-applications
// (DB kaydı + e-posta bildirimi). "website" alanı honeypot'tur — görünmez,
// gerçek kullanıcı doldurmaz; botları eler.
interface CareerPayload {
  name: string;
  email: string;
  phone?: string;
  position: string;
  message: string;
  cv_url?: string;
  website?: string;
}

const POSITIONS = [
  {value: 'icerik-editoru', label: message('İçerik Editörü')},
  {value: 'stajyer', label: message('Stajyer')},
  {value: 'sosyal-medya', label: message('Sosyal Medya')},
  {value: 'yazilim', label: message('Yazılım / Teknik')},
  {value: 'diger', label: message('Diğer')},
];

export function KariyerPage() {
  const {trans} = useTrans();
  const form = useForm<CareerPayload>({
    defaultValues: {position: 'icerik-editoru'},
  });

  const submit = useMutation({
    mutationFn: (payload: CareerPayload) =>
      apiClient.post('career-applications', payload).then(r => r.data),
    onSuccess: () => {
      toast(
        trans(
          message(
            'Başvurunuz alındı. En kısa sürede sizinle iletişime geçeceğiz.',
          ),
        ),
      );
      form.reset();
    },
    onError: err => onFormQueryError(err, form),
  });

  return (
    <SitePageLayout>
      <StaticPageTitle>
        <Trans message="Kariyer" />
      </StaticPageTitle>
      <div className="container mx-auto px-16 pb-40 pt-96 md:px-24 md:pt-112">
        <div className="mx-auto max-w-680">
          <h1 className="text-[28px] font-extrabold leading-tight text-main md:text-[34px]">
            <Trans message="CINE5'te Çalışın" />
          </h1>
          <p className="mt-10 text-[15px] leading-relaxed text-muted">
            <Trans message="Türkiye'nin film ve dizi platformunu birlikte büyütelim. İçerik editörlüğü, stajyerlik ya da farklı bir iş birliği için aşağıdaki formu doldurun — başvurunuz doğrudan ekibimize ulaşır." />
          </p>

          <div className="mt-28 rounded-2xl border border-white/10 bg-paper/60 p-20 md:p-28">
            <Form
              form={form}
              onSubmit={value => submit.mutate(value)}
            >
              <div className="grid gap-20 md:grid-cols-2">
                <FormTextField
                  name="name"
                  required
                  minLength={3}
                  label={<Trans message="Ad Soyad" />}
                />
                <FormTextField
                  name="email"
                  required
                  type="email"
                  label={<Trans message="E-posta" />}
                />
                <FormTextField
                  name="phone"
                  type="tel"
                  label={<Trans message="Telefon (isteğe bağlı)" />}
                />
                <FormSelect
                  name="position"
                  selectionMode="single"
                  required
                  label={<Trans message="Başvurulan Pozisyon" />}
                >
                  {POSITIONS.map(p => (
                    <Option key={p.value} value={p.value}>
                      <Trans {...p.label} />
                    </Option>
                  ))}
                </FormSelect>
              </div>
              <FormTextField
                name="cv_url"
                type="url"
                className="mt-20"
                label={<Trans message="CV / Portfolyo bağlantısı (isteğe bağlı)" />}
                description={
                  <Trans message="Google Drive, LinkedIn veya benzeri bir bağlantı paylaşabilirsiniz." />
                }
              />
              <FormTextField
                name="message"
                required
                minLength={20}
                inputElementType="textarea"
                rows={6}
                className="mt-20"
                label={<Trans message="Kendinizden bahsedin" />}
                description={
                  <Trans message="Deneyiminiz, ilgi alanlarınız ve neden CINE5 ekibinde olmak istediğiniz." />
                }
              />
              {/* Honeypot — görünmez; botlar doldurur, insanlar görmez. */}
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="absolute -left-[9999px] h-1 w-1 opacity-0"
                {...form.register('website')}
              />
              <Button
                type="submit"
                variant="flat"
                color="primary"
                size="md"
                className="mt-24 min-w-160"
                disabled={submit.isPending}
              >
                <Trans message="Başvuruyu Gönder" />
              </Button>
            </Form>
          </div>

          <p className="mt-16 text-xs leading-relaxed text-muted">
            <Trans message="Gönderdiğiniz bilgiler yalnızca işe alım değerlendirmesi için kullanılır ve üçüncü kişilerle paylaşılmaz." />
          </p>
        </div>
      </div>
    </SitePageLayout>
  );
}
